<!-- 
Listing of overrides for a levy collection.
-->
<template>
  <div>
    <v-card class="home">
      <our-table-header 
        :listing="listing"
        :has-export="hasExport"
        @click:refresh="refresh" 
        @update:pageNumber="updatePageNumber"
        @click:export="exportToExcel"
      />
      <our-table-busy-indicator :show="listing.loading"/>
      <v-data-table 
        :headers="headers" 
        :items="listing.data" 
        :pagination="listing.paging" 
        :total-items="listing.paging.totalItems" 
        :rows-per-page-items="rowsPerPageItems" 
        :no-data-text="noDataText"
        class="table-expandable"
        must-sort 
        hide-actions
        @click:refresh="refresh" 
        @update:pagination="updatePagination"
      >
        <template slot="items" slot-scope="props">
          <tr class="noselect">
            <our-nav-col :row="props.item.type"/>
            <td class="pa-0 ma-0 text-xs-center">
              <span v-if="showBringIntoScopeWarning(props.item)">
                <v-tooltip bottom>
                  <v-icon slot="activator" color="info" class="pa-0 ma-0">hourglass_empty</v-icon>
                  <span>Pending: provider will be brought into scope by the next run of the "import in-scope providers" task</span>
                </v-tooltip>
              </span>
              <span v-if="showTakeOutOfScopeWarning(props.item)">
                <v-tooltip bottom>
                  <v-icon slot="activator" color="info" class="pa-0 ma-0">hourglass_empty</v-icon>
                  <span>Pending: provider will be taken out of scope by the next run of the "import in-scope providers" task</span>
                </v-tooltip>
              </span>
              <span v-if="showMissingWarning(props.item)">
                <v-tooltip bottom>
                  <v-icon slot="activator" color="error" class="pa-0 ma-0">error_outline</v-icon>
                  <span>Orphaned: provider is not in-scope for this levy collection</span>
                </v-tooltip>
              </span>
            </td>
            <td>{{ props.item.providerName }} ({{ props.item.providerCode }})</td>
            <td>{{ props.item.description }}</td>
            <td>{{ props.item.explanation }}</td>
            <td>{{ props.item.created | compactDateTime }}</td>
            <td>{{ props.item.updated | compactDateTime }}</td>
            <td class="text-xs-center px-2">
              <v-tooltip v-if="!props.item.missing" bottom>
                <v-btn 
                  slot="activator" 
                  :to="`/levy-collections/${levyCollectionId}/providers/${props.item.providerId}`" 
                  icon class="ma-0 primary--text"
                >
                  <v-icon>school</v-icon>
                </v-btn>
                <span>Click to open the levy collection provider</span>
              </v-tooltip>
              <v-tooltip v-if="canDelete(props.item)" bottom>
                <v-btn 
                  slot="activator" 
                  icon class="ma-0 primary--text"
                  @click="deleteOverride(props.item)"
                >
                  <v-icon>playlist_add</v-icon>
                </v-btn>
                <span>Click to add the provider back to the levy collection</span>
              </v-tooltip>
            </td>
          </tr>
        </template>
      </v-data-table>
      <our-table-footer :paging="listing.paging" @update:pageNumber="updatePageNumber" @update:rowsPerPage="updateRowsPerPage" />
    </v-card>
    <override-delete-dialog 
      v-model="showDeleteOverrideDialog" 
      :levy-collection-id="levyCollectionId" 
      :override-id="overrideIdForDeletion"
      :prompt="deletionPrompt"
      button-text="Add"
      button-color="success"
      title="Add provider back to levy collection"
      @changed="redirect"
    />
  </div>
</template>

<script>
import tableMixins from "@/mixins/table.mixins";
import filterMixins from "@/mixins/filter.mixins";
import lookupMixins from "@/mixins/lookup.mixins";
import overrideMixins from "@/mixins/override.mixins";
import * as action from "@/store/levy-collection-overrides/action-types";
import * as mutation from "@/store/levy-collection-overrides/mutation-types";
import OverrideDeleteDialog from "@/components/levy-collection-overrides/OverrideDeleteDialog.vue";
import { LEVY_COLLECTION_OVERRIDES_EXPORT } from "@/store/claim-types";

export default {
  components: { OverrideDeleteDialog },
  mixins: [tableMixins, filterMixins, lookupMixins, overrideMixins],
  props: {
    levyCollectionId: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      action: action.LIST,
      exportAction: action.EXPORT,
      pagingMutation: mutation.LIST_SET_PAGING,
      expandMutation: mutation.LIST_TOGGLE_EXPAND,
      showDeleteOverrideDialog: false,
      overrideIdForDeletion: null,
      providerIdForDeletion: null,
      deletionPrompt: null,
      defaultNoDataText: "This levy collection does not have any overrides yet."
    };
  },
  computed: {
    headers() {
      return [
        { text: "", value: "Id", class: "navCol", width: 0, sortable: false },
        { text: "", value: "Warnings", sortable: false, width: 10 },
        { text: "Provider", value: "Provider.Name", sortable: true, width: 500 },
        { text: "Description", sortable: false },
        { text: "Explanation", sortable: false },
        { text: "Created", value: "Created", sortable: true },
        { text: "Updated", value: "Updated", sortable: true },
        { text: "", value: "Id", sortable: false, align: "right" }
      ];
    },
    state() {
      return this.$store.state.levyCollectionOverrides;
    },
    listing() {
      return this.state.listing;
    },
    loaded() {
      return this.listing.loaded;
    },
    hasExport() {
      return this.$store.getters.claims.includes(LEVY_COLLECTION_OVERRIDES_EXPORT);
    }
  },
  watch: {
    levyCollectionId() {
      this.reload();
    },
    loaded() {
      this.reload();
    }
  },
  mounted() {
    this.reload();
  },
  methods: {
    reload() {
      const needsReloaded =
        (!this.listing.loaded && !this.listing.loading) || this.state.levyCollectionId != this.levyCollectionId;
      if (needsReloaded) {
        this.$store.commit(mutation.LIST_SET_FILTERS, { levyCollectionId: this.levyCollectionId });
        this.populate();
      }
    },
    showTakeOutOfScopeWarning(override) {
      return !override.missing && override.category == "RemoveProvider";
    },
    showBringIntoScopeWarning(override) {
      return override.missing && override.category == "AddProvider";
    },
    showMissingWarning(override) {
      return override.missing && override.category != "RemoveProvider";
    },
    canDelete(override) {
      return override.category == "RemoveProvider" && this.canEditOverrides(override.providerStatus);
    },
    deleteOverride(override) {
      this.overrideIdForDeletion = override.id;
      this.providerIdForDeletion = override.providerId;
      this.deletionPrompt = `Add ${override.providerName} back to the levy collection?`;
      this.showDeleteOverrideDialog = true;
    },
    redirect() {
      this.$router.push(`/levy-collections/${this.levyCollectionId}/providers/${this.providerIdForDeletion}`);
    }
  }
};
</script>
